import React from "react";
import {
  ArrowLeft,
  XCircle,
  Archive,
  History,
  Loader2,
  PlayCircle,
  EllipsisVertical,
  RefreshCw,
} from "lucide-react";

import { TarefaDTO } from "@/types/tarefa.type";
import { Button, buttonVariants } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { TopBarSection } from "@/components/shared/SecaoBarraSuperior";
import { TarefaStatusBadge } from "@/components/tarefa/TarefaStatusBadge";
import { TarefaStatus } from "@workflow/types";

interface TarefaHeaderProps {
  tarefa: TarefaDTO;
  isActionLoading: boolean;
  /** Admin, criador ou responsável — quem pode mudar o status (fonte única na página). */
  canManageStatus: boolean;
  onAction: (action: "concluir" | "cancelar" | "reabrir" | "emExecucao") => void;
  onBack: () => void;
  onOpenHistorico: () => void;
}

export const TarefaHeader: React.FC<TarefaHeaderProps> = ({
  tarefa,
  isActionLoading,
  canManageStatus,
  onAction,
  onBack,
  onOpenHistorico,
}) => {
  const status = tarefa.status;

  // 1. Estados da Tarefa
  const isPendente = status === TarefaStatus.PENDENTE;
  const isEmExecucao = status === TarefaStatus.EM_EXECUCAO;
  const isConcluido = status === TarefaStatus.CONCLUIDO;
  const isCancelado = status === TarefaStatus.CANCELADO;
  const isArquivado = status === TarefaStatus.ARQUIVADO;

  // 2. Lógica de Exibição dos Botões (Status + Permissão)
  // A conclusão fica no painel de conclusão (respeita NENHUMA/FORM/CTA), não aqui.
  const canCancel = (isPendente || isEmExecucao) && canManageStatus;
  const canArchive = (isConcluido || isCancelado) && canManageStatus;
  const canEdit = isPendente && canManageStatus; // Ação "Em Execução"
  const canReopen = (isConcluido || isCancelado) && canManageStatus;

  return (
    <TopBarSection
      entity="Tarefa"
      entityBadge={<TarefaStatusBadge status={status} />}
      title={tarefa.nome}
      showButton
      buttonLabel="Voltar"
      buttonIcon={<ArrowLeft />}
      onButtonClick={onBack}
      dropdownActions={
        <DropdownMenu>
          <DropdownMenuTrigger
            className={buttonVariants({
              className: "w-full shadow-sm sm:w-auto",
              variant: "outline",
              size: "sm",
            })}
            disabled={isActionLoading}
          >
            <EllipsisVertical className="mr-2 h-4 w-4" /> Ações
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="min-w-48 border-border bg-popover">
            <DropdownMenuGroup>
              <DropdownMenuLabel>Opções da Tarefa</DropdownMenuLabel>
              <DropdownMenuSeparator />

              <DropdownMenuItem onClick={onOpenHistorico} className="cursor-pointer">
                <History className="mr-2 h-4 w-4 text-muted-foreground" /> Ver Histórico
              </DropdownMenuItem>

              {(canCancel || canReopen) && <DropdownMenuSeparator />}

              {/* Cancelar: Disponível se não estiver fechada */}
              {canCancel && (
                <DropdownMenuItem
                  onClick={() => onAction("cancelar")}
                  className="cursor-pointer text-red-500 focus:bg-red-50 focus:text-red-600 dark:focus:bg-red-950/30"
                >
                  <XCircle className="mr-2 h-4 w-4" /> Cancelar Tarefa
                </DropdownMenuItem>
              )}

              {canReopen && (
                <DropdownMenuItem
                  onClick={() => onAction("reabrir")}
                  className="cursor-pointer text-emerald-600 focus:bg-emerald-50 focus:text-emerald-700 dark:text-emerald-400 dark:focus:bg-emerald-900/20 dark:focus:text-emerald-300"
                >
                  <RefreshCw className="mr-2 h-4 w-4" /> Reabrir Tarefa
                </DropdownMenuItem>
              )}
            </DropdownMenuGroup>
          </DropdownMenuContent>
        </DropdownMenu>
      }
      // --- BOTÕES PRINCIPAIS (Avanço de Fluxo) ---
      extraActions={
        <div className="mt-4 flex w-full flex-col items-stretch gap-2 sm:mt-0 sm:w-auto sm:flex-row sm:items-center">
          {canEdit && (
            <Button
              onClick={() => onAction("emExecucao")}
              disabled={isActionLoading}
              size="sm"
              className="w-full bg-emerald-600 text-white shadow-sm shadow-emerald-600/20 transition-all hover:bg-emerald-700 dark:bg-emerald-600 dark:hover:bg-emerald-500 sm:w-auto"
            >
              {isActionLoading ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <PlayCircle className="mr-2 h-4 w-4" />
              )}
              Em Execução
            </Button>
          )}

          {isArquivado && (
            <Badge
              variant="secondary"
              className="flex h-10 items-center border-border/50 bg-muted/50 px-3 py-1.5 font-medium text-muted-foreground"
            >
              <Archive className="mr-2 h-3.5 w-3.5 opacity-70" /> Arquivada
            </Badge>
          )}
        </div>
      }
    />
  );
};
